import { BaseNode, type NodeConfig } from 'mflows';
import axios, {type AxiosRequestConfig,type AxiosResponse } from 'axios';

// 定义HTTP请求节点的配置类型
interface HttpRequestConfig extends NodeConfig {
  url: string;
  method: string;
  headers?: Record<string, string>;
  data?: any;
  timeout?: number;
}

// 定义HTTP响应数据类型
interface HttpResponseData {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  data: any;
  config: AxiosRequestConfig;
}

// 定义payload数据类型
interface HttpPayload {
  url?: string;
  method?: string;
  headers?: Record<string, string>;
  timeout?: number;
  value?: any;
}

export class HttpRequest extends BaseNode<HttpRequestConfig, HttpResponseData> {
  static NodeType = 'HTTP请求';

  protected async initialize(): Promise<void> {
    // 定义输入连接点
    this.setInputPorts([
      { id: 'execute', name: '执行', allowTypes: ['flow'], dataType: 'flow' },
      { id: 'payload', name: '输入', allowTypes: ['object','customFunction'], dataType: 'object' },
      { id: 'http-headers', name: '请求头', allowTypes: ['object'], dataType: 'object' },
      { id: 'http-data', name: '请求体', allowTypes: ['object'], dataType: 'object' }
    ]);

    this.setOutputPorts([
      { id: 'http-response', name: '响应', dataType: 'object' },
      { id: 'http-error', name: '错误', dataType: 'object' },
      { id: 'next', name: '下一步', dataType: 'flow' }
    ]);
  }

  protected receiveData(pointId: string, data: unknown): void {
    void pointId; void data;
    console.log('receiveData', pointId, data);
    
    // 根据不同的输入点更新配置（仅做数据传递与配置合并）
    switch (pointId) {
      case 'payload':
        if (typeof data === 'object' && data !== null) {
          const payload = data as HttpPayload;
          // 合并payload中的配置，优先使用节点自身的配置
          const updates: Partial<HttpRequestConfig> = {};
          
          if (payload.url && !this.settings.url) {
            updates.url = payload.url;
          }
          if (payload.method && !this.settings.method) {
            updates.method = payload.method;
          }
          if (payload.headers && Object.keys(payload.headers).length > 0) {
            updates.headers = {
              ...this.settings.headers,
              ...payload.headers
            };
          }
          if (payload.timeout && !this.settings.timeout) {
            updates.timeout = payload.timeout;
          }
          // 使用payload的value作为请求数据
          if (payload.value !== undefined) {
            updates.data = payload.value;
          }

          if (Object.keys(updates).length > 0) {
            this.updateSettings(updates);
          }
        }
        break;
      case 'http-headers':
        if (typeof data === 'object' && data !== null) {
          // 合并请求头，优先使用节点自身的配置
          const headers = {
            ...this.settings.headers,
            ...(data as Record<string, string>)
          };
          this.updateSettings({ headers });
        }
        break;
      case 'http-data':
        if (data !== null) {
          this.updateSettings({ data });
        }
        break;
      default:
        // 其他输入口暂不处理
        break;
    }

    // 当接收到 flow 类型输入端口（如 execute）时才触发执行；数据类型仅完成数据传递
    const inputPoint = this.findInputPort(pointId);
    if (inputPoint?.dataType === 'flow') {
      // 触发当前配置下的请求执行
      this.executeConfigUpdate();
    }
  }

  protected checkConfig(): boolean {
    // 检查配置是否有效，至少需要URL和请求方法
    return Boolean(this.settings.url) && Boolean(this.settings.method);
  }

  protected async processConfigUpdate(settings: HttpRequestConfig): Promise<any> {
    try {
      // 构建请求配置
      const requestConfig: AxiosRequestConfig = {
        url: settings.url,
        method: settings.method,
        headers: settings.headers || {},
        timeout: settings.timeout || 10000
      };

      // 处理请求数据
      const method = settings.method.toLowerCase();
      if (method === 'get') {
        // GET请求使用params
        requestConfig.params = settings.data;
      } else {
        // 其他请求使用data
        requestConfig.data = settings.data;
      }

      // 发送HTTP请求
      const response: AxiosResponse = await axios(requestConfig);

      // 构建响应数据
      const responseData: HttpResponseData = {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers as Record<string, string>,
        data: response.data,
        config: response.config
      };

      // 更新节点数据
      Object.assign(this.nodeData, { value: responseData });

      return this.nodeData;
    } catch (error) {
      // 将错误转换为可序列化的对象并以 NodeData 形式返回（走 dataChange 分发）
      if (axios.isAxiosError(error)) {
        const errorData: any = {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        };
        return {
          value: errorData,
          error: new Error(JSON.stringify(errorData)),
          state: 'error',
          updateTime: Date.now()
        };
      }
      return {
        value: { message: String(error) },
        error: error instanceof Error ? error : new Error(String(error)),
        state: 'error',
        updateTime: Date.now()
      };
    }
  }

  // 覆盖输出端口行为：
  // - 成功：仅分发到 http-response，并触发 next
  // - 错误：仅分发到 http-error，不触发 next
  protected getOutputPortActions(): { dataOnly?: string[]; triggerFlow?: string[] } {
    if (this.getState() === 'error') {
      return { dataOnly: ['http-error'], triggerFlow: [] };
    }
    return { dataOnly: ['http-response'], triggerFlow: ['next'] };
  }
}
